// ============================================================
// RAM - a vehicle's charge (plan map-display, Track D; the vehicle rulings
// of 2026-10-07).
//
// A ram is a charge: a straight run-up along the heading the driver makes on
// the map, ending in contact with a target off the bow. The target may Dodge
// (never Parry); the impact is the GM's call - no house rule computes it: the
// GM types the damage the target takes and the share the bow takes. What
// this file owns is the pure part: whether the target is off the bow (contact
// is the drum's `requiresContact`, shared with the body's own actions), the
// stand-in weapon the strike is resolved with, and reading the ruling.
//
// Pure. Zero React dependency.
// ============================================================
import { arcFor, bearingBetween, type ArcPreset } from '../battlefield/facing';
import type { Token } from '../battlefield/tokens';
import { emptySlot } from '../factories';
import type { Slot } from '../../types';

type Placed = Pick<Token, 'x' | 'z' | 'heading'>;

/**
 * Is the target NOT off the rammer's bow - beside or behind the hull? False
 * when the map cannot say: either marker missing (the caller hands none
 * when the map may not decide), or a hull never turned.
 */
export function notOffBow(rammer: Placed | undefined, target: Placed | undefined, preset: ArcPreset): boolean {
  if (!rammer || !target) return false;
  const arc = arcFor(rammer.heading, bearingBetween(rammer, target), preset);
  return arc !== null && arc !== 'front';
}

/**
 * The stand-in weapon a ram strikes with: the GM's damage, Impact, no
 * penetration, never parried (Flexible); `ramBow` is the bow's share, taken
 * when the hit lands (hooks/useApplyHits) - a ram the target Dodges costs the
 * bow nothing.
 */
export function ramSlot(damage: number, bow = 0): Slot {
  return {
    ...emptySlot(0),
    name: 'Ram',
    weaponName: 'Ram',
    weaponType: 'melee',
    damageExpression: String(damage),
    damageType: 'I',
    penetration: 0,
    range: 0,
    rateOfFire: { single: true, semi: 0, full: 0 },
    specialRules: ['Flexible'],
    ammo: { current: 0, max: 0, reloadAction: 'N/A' },
    isEmpty: false,
    ramBow: bow,
  };
}

/** The GM's ruling on a ram: whole wounds to the target and to the bow. */
export interface RamRuling {
  damage: number;
  bow: number;
}

const wounds = (v: unknown): number | null => {
  if (v === '' || v === undefined || v === null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : null;
};

/** A ruling as an answer brings it back (any shape), or null when it is not one. */
export function readRamRuling(value: unknown): RamRuling | null {
  if (!value || typeof value !== 'object') return null;
  const { damage, bow } = value as Record<string, unknown>;
  const d = wounds(damage);
  const b = wounds(bow);
  return d === null || b === null ? null : { damage: d, bow: b };
}

/** The ask a player's ram puts to the GM: who rams whom. The GM answers with a `RamRuling`. */
export const RAM_ASK = 'ram';

/** What the GM is asked: the rammer's and the target's names, as this screen shows them. */
export interface RamAsk {
  rammer: string;
  target: string;
}

/** A ram ask's payload as it comes off the wire, or null when it is not one. */
export function readRamAsk(payload: unknown): RamAsk | null {
  if (!payload || typeof payload !== 'object') return null;
  const { rammer, target } = payload as Record<string, unknown>;
  return typeof rammer === 'string' && typeof target === 'string' ? { rammer, target } : null;
}
